import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { NgTemplateOutlet, ViewportScroller } from '@angular/common';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  ChevronDownIconComponent,
  SearchIconComponent,
  EditIconComponent,
} from '@kuiper/icons';
import { debounceTime, skip } from 'rxjs';
import { DepartmentDetails } from '../department-model';
import { DepartmentService } from '../department.service';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { AvatarPipe } from '../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { LoaderComponent } from '../../../../../../ui-components/lib/loader/loader.component';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { SearchMinLength } from '../../../../../common/enums/search-min-length';
import { Option } from '../../../../../common/models/option.model';
import { DepartmentPosition } from '../department-model';
import { DepartmentPositionCardComponent } from '../department-position-card/department-position-card.component';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

export const DEPARTMENT_DETAILS_POSITIONS_PAGE_SIZE = 12;

@Component({
  selector: 'lib-department-details',
  imports: [
    NgTemplateOutlet,
    OverlayLoaderComponent,
    AvatarPipe,
    GroupUserCardComponent,
    PaginatorComponent,
    TranslocoPipe,
    FormsModule,
    LoaderComponent,
    SearchIconComponent,
    ChevronDownIconComponent,
    EditIconComponent,
    TenantPathPipe,
    RouterLink,
    DepartmentPositionCardComponent,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './department-details.component.html',
  styleUrl: './department-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DepartmentDetailsComponent implements OnInit {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();

  route = inject(ActivatedRoute);
  departmentService = inject(DepartmentService);
  router = inject(Router);
  tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);

  isLoading = signal<boolean>(true);
  isUsersLoading = signal<boolean>(true);
  departmentDetails = model.required<DepartmentDetails>();
  departmentId = signal<string>('');
  departmentUsersCards = signal<Option[]>([]);

  page = signal<number>(1);
  currentPage = signal<number>(1);
  pageSize = signal<number>(12);
  totalPages = signal<number>(20);
  totalItems = signal<number>(0);
  searchTerm = signal<string>('');
  private readonly searchTerm$ = toObservable(this.searchTerm);
  isMoreThanThreeCharacters = signal<boolean>(false);
  minSearchLength = signal<number>(SearchMinLength.length);

  private readonly featureFlagService = inject(FeatureFlagsService);
  readonly isDepartmentsUpdateEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.departments_update
    )
  );
  viewportScroller = inject(ViewportScroller);
  positions = signal<DepartmentPosition[]>([]);
  isPositionsLoading = signal<boolean>(true);
  hasPositionsError = signal<boolean>(false);
  positionsPage = signal<number>(1);
  positionsTotalPages = signal<number>(1);
  positionsTotal = signal<number>(0);
  private readonly isPageSettled = computed(() => {
    const pageReady = !this.isLoading() && !this.isUsersLoading();
    if (!this.isDepartmentsUpdateEnabled()) {
      return pageReady;
    }

    return pageReady && !this.isPositionsLoading();
  });
  private hasScrolledToPositions = false;

  constructor() {
    afterRenderEffect({
      read: () => {
        if (
          this.hasScrolledToPositions ||
          this.route.snapshot?.fragment !== 'positions' ||
          !this.isPageSettled()
        ) {
          return;
        }
        this.hasScrolledToPositions = true;
        this.viewportScroller.scrollToAnchor('positions');
      },
    });
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.departmentId.set(params['id']);
        this.getDepartmentById(params['id']);
      });
    this.getDepartmentCards();
    if (this.isDepartmentsUpdateEnabled()) {
      this.getDepartmentPositions();
    } else {
      this.isPositionsLoading.set(false);
    }

    this.searchTerm$
      .pipe(skip(1), debounceTime(600), takeUntilDestroyed(this.destroyRef))
      .subscribe(term => {
        this.handleSearchTerm(term);
      });
  }

  getDepartmentById(id: string) {
    this.departmentService
      .getDepartment(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: { data: any }) => {
          this.departmentDetails.set(response.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.router.navigate([
            this.tenantService.adminPath(`${ADMIN_ROUTES.DEPARTMENTS.LIST}`),
          ]);
        },
      });
  }

  handleSearchTerm(term: string): void {
    this.currentPage.set(1);
    if (term.length >= this.minSearchLength()) {
      this.isMoreThanThreeCharacters.set(true);
      this.getDepartmentCards(term);
    } else if (
      this.isMoreThanThreeCharacters() &&
      term.length < this.minSearchLength()
    ) {
      this.isMoreThanThreeCharacters.set(false);
      this.getDepartmentCards();
    }
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  getDepartmentCards(searchName = '') {
    this.isUsersLoading.set(true);
    this.departmentService
      .getDepartmentUsers(this.departmentId(), this.currentPage(), searchName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const users = res.data.map((user: any) => {
            return {
              label: user.name,
              value: user.id.toString(),
              image: user.image,
              job_title: user.position?.job_title?.name || '',
            };
          });
          this.totalPages.set(res.meta.last_page);
          this.totalItems.set(res.meta.total);
          this.departmentUsersCards.set(users);
        },
        error: () => {
          this.isUsersLoading.set(false);
        },
        complete: () => {
          this.isUsersLoading.set(false);
        },
      });
  }

  getDepartmentPositions() {
    this.isPositionsLoading.set(true);
    this.hasPositionsError.set(false);
    this.departmentService
      .getDepartmentPositions(
        this.departmentId(),
        this.positionsPage(),
        DEPARTMENT_DETAILS_POSITIONS_PAGE_SIZE
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.positions.set(response.data);
          this.positionsTotalPages.set(response.meta.last_page);
          this.positionsTotal.set(response.meta.total);
          this.isPositionsLoading.set(false);
        },
        error: () => {
          this.hasPositionsError.set(true);
          this.isPositionsLoading.set(false);
        },
      });
  }

  handlePositionsPagination(page: number) {
    this.positionsPage.set(page);
    this.getDepartmentPositions();
  }

  goBack() {
    history.back();
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getDepartmentCards();
  }
}
